import React from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { GT_COLORS } from '../groups-teams/shared/constants';
import { RoutingOverview, RoutingPerson } from '../../types/notification-rules.types';

/**
 * Who gets an alert that has no one else to go to, such as one from a team
 * with no leader, or one whose chosen people have no email, text or call set up.
 *
 * Shown as a single choice that defaults to "All admins", because that is what
 * every organization gets today. Picking one person replaces the admins.
 */

const ALL_ADMINS = '__all_admins__';

interface Props {
    overview: RoutingOverview;
    disabled?: boolean;
    /** null means "all admins". */
    onChange: (userId: string | null) => void;
}

export const PointOfContactSelect: React.FC<Props> = ({ overview, disabled, onChange }) => {
    const adminCount = overview.backstop.adminCount;
    const allAdmins: RoutingPerson = {
        userId: ALL_ADMINS,
        name: adminCount > 0 ? `All admins (${adminCount})` : 'All admins (none have email, text or call set up)',
        email: null,
        methods: [],
        reachable: adminCount > 0,
    };
    const options = [allAdmins, ...overview.systemUsers];
    const value = overview.pointOfContact
        ? options.find((o) => o.userId === overview.pointOfContact!.userId) ?? overview.pointOfContact
        : allAdmins;

    return (
        <Autocomplete<RoutingPerson, false, true>
            size="small"
            disableClearable
            disabled={disabled}
            options={options}
            value={value}
            onChange={(_, picked) => onChange(picked.userId === ALL_ADMINS ? null : picked.userId)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.userId === v.userId}
            filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                // Typing should search people; the selected label should not filter the list.
                if (!q || q === value.name.toLowerCase()) return opts;
                return opts.filter((o) => o.name.toLowerCase().includes(q) || (o.email ?? '').toLowerCase().includes(q));
            }}
            sx={{ width: { xs: '100%', sm: 340 }, flexShrink: 0 }}
            slotProps={{ popper: { placement: 'bottom-start', sx: { minWidth: 320 } } }}
            renderInput={(params) => <TextField {...params} aria-label="Point of contact" />}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.userId} sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: '13px', fontWeight: 500 }}>{option.name}</Typography>
                        <Typography noWrap sx={{ fontSize: '11px', color: option.reachable ? GT_COLORS.textFaint : GT_COLORS.red }}>
                            {option.userId === ALL_ADMINS
                                ? 'Everyone with an admin role'
                                : option.reachable
                                    ? (option.email ?? '')
                                    : 'No email, text or call set up'}
                        </Typography>
                    </Box>
                </Box>
            )}
        />
    );
};
